import React, { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { iaApi } from '../../../lib/api';
import { Download, FileText, Trash2, Upload } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';

export const CartaOfertaDialog = ({
  isOpen,
  setIsOpen,
  selectedUnidad,
  onReload,
  toast,
}) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const cartaActual = selectedUnidad?.carta_oferta_archivo;

  const handleClose = (open) => {
    setIsOpen(open);
    if (!open) setFile(null);
  };

  const handleUpload = async () => {
    if (!file || !selectedUnidad) return;
    setUploading(true);
    try {
      await iaApi.uploadCartaOferta(selectedUnidad.unidad_id, file);
      toast.success(cartaActual ? 'Carta oferta reemplazada' : 'Carta oferta cargada');
      setFile(null);
      onReload?.();
      setIsOpen(false);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Error al subir la carta oferta');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async () => {
    if (!selectedUnidad) return;
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(iaApi.downloadCartaOfertaUrl(selectedUnidad.unidad_id), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al descargar');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = cartaActual?.filename || 'carta-oferta';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      toast.error('Error al descargar el archivo');
    }
  };

  const handleDelete = async () => {
    if (!selectedUnidad) return;
    if (!confirm('¿Eliminar la carta oferta de esta unidad? La escritura no se verá afectada.')) return;
    setDeleting(true);
    try {
      await iaApi.deleteCartaOferta(selectedUnidad.unidad_id);
      toast.success('Carta oferta eliminada');
      onReload?.();
      setIsOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="carta-oferta-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-amber-600" />
            Carta Oferta - {selectedUnidad?.nombre_unidad}
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-1">
            Documento firmado con el propietario al momento de la compra de la unidad.
            Es independiente de la escritura.
          </p>
        </DialogHeader>

        <div className="space-y-4">
          {cartaActual ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="w-5 h-5 text-amber-600 shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium text-amber-900 truncate" data-testid="carta-oferta-filename">
                      {cartaActual.filename}
                    </p>
                    <p className="text-xs text-amber-700">
                      {cartaActual.size ? `${(cartaActual.size / 1024).toFixed(1)} KB` : ''}
                      {cartaActual.uploaded_at && (
                        <> · Cargado: {new Date(cartaActual.uploaded_at).toLocaleDateString('es-AR')}</>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownload}
                    data-testid="carta-oferta-download-btn"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Descargar
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:bg-red-50"
                    onClick={handleDelete}
                    disabled={deleting}
                    data-testid="carta-oferta-delete-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-500 text-center py-2">
              No hay carta oferta cargada para esta unidad.
            </div>
          )}

          <div className="space-y-2">
            <Label>{cartaActual ? 'Reemplazar carta oferta' : 'Cargar carta oferta'}</Label>
            <Input
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="carta-oferta-file-input"
            />
            {file && (
              <p className="text-xs text-slate-600">
                Archivo seleccionado: <span className="font-medium">{file.name}</span>
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cerrar
          </Button>
          <Button
            disabled={!file || uploading}
            onClick={handleUpload}
            data-testid="carta-oferta-upload-btn"
          >
            <Upload className="w-4 h-4 mr-1" />
            {uploading ? 'Subiendo...' : (cartaActual ? 'Reemplazar' : 'Cargar')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
